import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - i + 2);

export default function AddBreedingSeasonModal({ open, onClose, onSaved, horse, editing }) {
  const [year, setYear]             = useState(String(CURRENT_YEAR));
  const [months, setMonths]         = useState([]);
  const [bookedTo, setBookedTo]     = useState('');
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setYear(String(editing.year));
      setMonths(editing.months || []);
      setBookedTo(editing.booked_to || '');
    } else {
      setYear(String(CURRENT_YEAR));
      setMonths([]);
      setBookedTo('');
    }
  }, [open, editing]);

  const toggleMonth = (m) =>
    setMonths(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { horse_id: horse.id, year: parseInt(year), months, booked_to: bookedTo };
      const api = (await import('../../services/api')).default;
      if (editing) {
        await api.put(`/breeding-seasons/${editing.id}`, payload);
      } else {
        await api.post('/breeding-seasons', payload);
      }
      onSaved();
      onClose();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Breeding Season' : 'Add Breeding Season'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label className="font-semibold">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="font-semibold">Breeding Months</Label>
            <div className="flex flex-wrap gap-1.5">
              {MONTHS.map(m => (
                <button key={m} type="button" onClick={() => toggleMonth(m)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    months.includes(m)
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}>{m}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <Label className="font-semibold">Booked to</Label>
            <Input value={bookedTo} onChange={e => setBookedTo(e.target.value)}
              placeholder="e.g. Justify, American Pharoah (if known)" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}
            className="bg-primary hover:bg-primary/90 text-white">
            {saving ? 'Saving…' : editing ? 'Update' : 'Add Season'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
